<template>
  <BaseDialog centered contentClass="llm-extraction-generate" ref="dialog" :show-actions="true" :onClose="cancelClick">
    <template slot="title">{{ $t('llm-extraction-generate-dialog.title') }}</template>
    <template slot="content">
      <div class="llm-extraction-generate__options">
        <FormRadioGroup v-model="selection" :options="options">
          <template #label="{ option }">
            <div v-if="option.value === 'useSameEvalGrid'">
              <div class="option-title">{{ $t('llm-extraction-generate-dialog.content-only') }}</div>
              <div class="option-subtitle">
                {{ $t('llm-extraction-generate-dialog.content-only-subtitle') }}
              </div>
            </div>
            <div v-else>
              <div class="option-title">{{ $t('llm-extraction-generate-dialog.model-and-content') }}</div>
              <BaseSelect
                class="option-dropdown"
                name="evaluationGridTemplate"
                :items="evaluationGridTemplates"
                :value="selectedTemplateId"
                item-text="name"
                item-value="id"
                :placeholder="$t('llm-extraction-generate-dialog.model-choice')"
                @change="onSelectTemplate"
                :isDisabled="isDisabled"
              />
              <div class="option-subtitle">
                {{ $t('llm-extraction-generate-dialog.model-and-content-subtitle') }}
              </div>
            </div>
          </template>
        </FormRadioGroup>

        <div class="rename-opportunity__actions">
          <FormButton is-outlined class="rename-opportunity__cancel" @click.native="cancelClick">{{
            $t('actions.cancel')
          }}</FormButton>
          <FormButton @click.native="onSubmit" type="submit" :disabled="isFormInvalid">
            <span>{{ $t('llm-extraction-generate-dialog.regenerate') }}</span>
          </FormButton>
        </div>
      </div>
    </template>
  </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/base/BaseDialog';
import FormButton from '@/components/form/FormButton';

import { mapActions, mapGetters } from 'vuex';
import FormRadioGroup from '@/components/form/FormRadioGroup.vue';
import BaseSelect from '@/components/base/BaseSelect.vue';

export default {
  name: 'LlmExtractionGenerateDialog',
  components: {
    BaseSelect,
    FormRadioGroup,
    BaseDialog,
    FormButton,
  },
  data() {
    return {
      opportunityId: null,
      selection: 'useSameEvalGrid',
      selectedTemplateId: null,
      hasSelectedTemplateId: false,
      isDisabled: false,
    };
  },
  computed: {
    ...mapGetters('evaluationGridTemplates', ['evaluationGridTemplates']),
    ...mapGetters('opportunity', ['opportunity']),
    ...mapGetters('evaluationGrid', ['evaluationFields']),
    ...mapGetters('fieldsExtraction', ['previousDocumentIds']),
    isFormInvalid() {
      return !this.opportunityId || (this.selection === 'useDifferentEvalGrid' && !this.selectedTemplateId);
    },
    options() {
      return [
        ...(!this.hasSelectedTemplateId
          ? [
              {
                value: 'useSameEvalGrid',
                class: 'llm-extraction-generate__option',
                selectedClass: 'option-selected',
              },
            ]
          : []),
        {
          value: 'useDifferentEvalGrid',
          class: 'llm-extraction-generate__option',
          selectedClass: 'dangerous-option-selected',
        },
      ];
    },
  },
  methods: {
    ...mapActions('fieldsExtraction', ['startExtraction']),
    ...mapActions('opportunity', ['getOpportunity']),
    openDialog(opportunityId, selectedTemplateId) {
      this.opportunityId = opportunityId;

      if (selectedTemplateId) {
        this.selectedTemplateId = selectedTemplateId;
        this.hasSelectedTemplateId = true;
        this.isDisabled = true;
      }
      this.$refs.dialog.openModal();
      this.$nextTick(() => {
        this.selection = selectedTemplateId ? 'useDifferentEvalGrid' : 'useSameEvalGrid';
      });
    },
    closeDialog() {
      this.opportunityId = null;
      this.selection = 'useSameEvalGrid';
      this.selectedTemplateId = null;
      this.hasSelectedTemplateId = false;
      this.isDisabled = false;
      this.$refs.dialog.isModalOpen = false;
    },
    onSelectTemplate(value) {
      this.selectedTemplateId = value;
    },
    async onSubmit() {
      if (!this.opportunityId || !this.opportunity) return;

      try {
        // Only send a templateId when the user explicitly switches to a different
        // grid. Sending the current templateId triggers the backend re-sync,
        // which cascade-deletes the existing evaluation_grid_field_value rows
        // (extractions AND annotations) — the exact opposite of what
        // "Régénérer le contenu seulement" promises in the UI copy.
        //
        // With templateId=null, the backend skips the re-sync, the existing
        // grid stays in place with its annotations, and save_all_extraction_results
        // at the end of the extraction replaces only the LLM_EXTRACTION rows.
        const templateId =
          this.selection === 'useDifferentEvalGrid' && this.selectedTemplateId ? this.selectedTemplateId : null;

        const hasPreviousSelection = this.previousDocumentIds.length > 0;

        this.startExtraction({
          opportunityId: this.opportunityId,
          evaluationGridTemplateId: templateId,
          documentIds: hasPreviousSelection ? this.previousDocumentIds : null,
          enableTriage: !hasPreviousSelection,
        });

        this.closeDialog();
      } catch {
        this.$toast.error(this.$t('toast.error'));
      }
    },
    handleKeyup(event) {
      const keyCode = event.keyCode || event.charCode;

      if (keyCode === 13) {
        if (!this.isFormInvalid) this.onSubmit();
      }
    },
    cancelClick() {
      this.closeDialog();
      this.$emit('on-cancel');
    },
  },
  created() {
    document.addEventListener('keyup', this.handleKeyup);
  },
  destroyed() {
    document.removeEventListener('keyup', this.handleKeyup);
  },
};
</script>

<style lang="scss">
.base-dialog__llm-extraction-generate {
  height: auto;
  width: 750px;
  position: relative;

  .base-dialog__header {
    padding-top: 24px;
  }
  .base-dialog__content {
    padding: 16px 24px 24px 24px !important;
    font-size: 18px;
    background: $color-white !important;
  }

  .close-icon__container {
    display: none;
  }
}

.llm-extraction-generate {
  &__options {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  &__option {
    display: flex;
    align-items: center;
    padding: 12px;
    border-radius: $border-radius-root;
    border: 2px solid $color-slate-200;
    cursor: pointer;
    margin-right: 0;

    .option-title {
      font-weight: 600;
      font-size: $font-size-root;
    }
    .option-subtitle {
      font-size: $font-size-small;
      color: $color-normal-pale;
    }
    .option-dropdown {
      margin: 12px 0;
      height: 42px;

      .v-input__slot {
        width: 400px;
        margin-bottom: 0px !important;
      }

      .v-text-field__details {
        display: none !important;
      }
    }

    &.option-selected {
      border-radius: $border-radius-root;
      border: 2px solid $color-accent-500;
      background: $color-accent-100;
    }
    &.dangerous-option-selected {
      border-radius: $border-radius-root;
      border: 2px solid $color-error;
      background: $color-error-pale;

      .option-subtitle,
      .accent--text {
        color: $color-error !important;
      }
    }
  }

  &__actions {
    margin-top: 32px;
    justify-content: flex-end;
    display: flex;
  }

  &__cancel {
    margin-right: 12px;
  }
}
</style>
